import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ConfigProvider } from './configContext';
import './styles/App.css';
import './styles/common.css';
import { Route, createBrowserRouter, createRoutesFromElements, Navigate, useRouteError, useRouteLoaderData } from 'react-router-dom';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { PageLayout } from './components/PageLayout';
import { CommitsView, CommitLoader } from './components/AuditorView.tsx';
import { ApiHelper } from './ApiHelper';
import { apiFetch } from './lib/api-client';
import { CommitDetails, commitDetailsLoader } from './components/CommitDetails';
import { ProfileList } from './components/Profiles.tsx';
import { ApiProfileList, apiLoader } from './components/DailyApiProfiles.tsx';
import { AdhocApiProfiles } from './components/AdhocApiProfiles.tsx'
import { AdhocProfile } from './components/AdhocProfile.tsx';
import { RequestTimeline, TimelineLoader } from './components/RequestTimeline';
import FAQPage from './components/FAQ.tsx';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { ComponentView } from './components/ComponentView.tsx';
import { ComponentLoader } from './components/ComponentLoader.ts';
import { ComponentDetails, ComponentDetailsLoader } from './components/ComponentDetails.tsx';
import { RedirectToNewPath } from './components/RedirectToNewPath.jsx';
import { BackFillView } from './components/BackFillView.tsx';
import { Card, Text } from '@fluentui/react-components';
import CopyDatabaseManual from './components/CopyDatabaseManual';
import { SettingsView } from './components/SettingsView.tsx';
import StackTracePage from './components/StackTrace.tsx';
import QueryBuilder from './components/QueryBuilder.tsx';
import {
    JobDefinitionsPage,
    AgentTaskDashboardPage,
    AutoPrDashboardPage,
    AgentTaskListPage,
    AgentTaskManagerLandingPage,
    AgentTaskPage,
    AgentTaskQuestionsPage,
    AgentTaskProposedChangesPage,
    AgentTaskSummaryPage,
    AgencyLogsPage,
    AgencyLogsArchivePage,
} from './components/AgentTaskManager.tsx';
import { AuthenticationFailure, SigningOn } from './components/AuthenticationBootstrap';
import { isAuthenticationRedirectInProgress } from './lib/msal';
import { ComputeInsightsPage } from './features/compute/ComputeInsightsPage';
import { ConfigDashboardPage } from './features/compute/ConfigDashboardPage';
import { SpoComputeLayout } from './features/compute/SpoComputeLayout';
import { COMPUTE_TABS, computePath } from './features/compute/computeRoutes';
import { HomePage } from './components/HomePage';

interface UserInfo {
    isAuditor?: boolean;
    [key: string]: unknown;
}

interface EnvironmentInfo {
    isDatabaseExtractEnabled: boolean;
    environment: string;
    [key: string]: unknown;
}

type AppConfig = Record<string, unknown>;

declare global {
    interface Window {
        user?: UserInfo | null;
    }
}

initializeIcons();

export function createRouter() {
    return createBrowserRouter(
        createRoutesFromElements(
            <>
                <Route element={<App />} loader={UserLoader} id="root" errorElement={<RootRouteError />}>
                    <Route
                        path="/CommitDetails/:webcommitId"
                        element={<RedirectToNewPath />}
                    />
                    <Route path="/service/:serviceName/commitDetails/:webcommitId" element={<CommitDetails />} loader={async ({ params }) => { return commitDetailsLoader(params.serviceName, params.webcommitId); }} />
                    <Route path="/" element={<HomePage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/service/:serviceName/allcommits" element={<CommitsView auditorView={true} />} loader={async ({ params, request }) => { return CommitLoader(params.serviceName, {request}); }} />
                    <Route path="/service/:serviceName/mycommits" element={<CommitsView auditorView={false} />} loader={async ({ params, request }) => { return CommitLoader(params.serviceName, {request}); }} />
                    <Route path="/service/:serviceName/profiles" element={<ProfileList />} />
                    <Route path="/service/:serviceName/agenttasks" element={<AgentTaskManagerLandingPage />} />
                    <Route path="/service/:serviceName/agenttasks/definitions" element={<JobDefinitionsPage />} />
                    <Route path="/service/:serviceName/agenttasks/list/:taskListId" element={<AgentTaskListPage />} />
                    <Route path="/service/:serviceName/agenttasks/dashboard" element={<AgentTaskDashboardPage />} />
                    <Route path="/service/:serviceName/agenttasks/autopr" element={<AutoPrDashboardPage />} />
                    <Route path="/service/:serviceName/agenttasks/summary" element={<AgentTaskSummaryPage />} />
                    <Route path="/service/:serviceName/agenttasks/questions" element={<AgentTaskQuestionsPage />} />
                    <Route path="/service/:serviceName/agenttasks/changes" element={<AgentTaskProposedChangesPage />} />
                    <Route path="/service/:serviceName/agenttasks/changes/:taskListId/:taskId" element={<AgentTaskProposedChangesPage />} />
                    <Route path="/service/:serviceName/agenttasks/task/:taskId" element={<AgentTaskPage />} />
                    <Route path="/service/:serviceName/agenttasks/logs/:taskListId/:taskId/:workItemId" element={<AgencyLogsPage />} />
                    <Route path="/service/:serviceName/agenttasks/archive/:taskListId/:taskId/:workItemId" element={<AgencyLogsArchivePage />} />
                    <Route path="/dailyapiprofiles" element={<ApiProfileList />} loader={apiLoader} />
                    <Route path="/adhocapiprofiler" element={<AdhocApiProfiles />} />
                    <Route path="/adhoc-profiles/:id" element={<AdhocProfile />} />
                    <Route path="/requesttimeline" element={<RequestTimeline />} loader={TimelineLoader} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/service/:serviceName/components" element={<ComponentView />} loader={ComponentLoader}/>
                    <Route path="/service/:serviceName/ComponentDetails/:componentId" element={<ComponentDetails />} loader={ComponentDetailsLoader} />
                    <Route path="/BackFill" element={<BackfillRedirect />} />
                    <Route path="/dev/mirror" element={<DatabaseCopyRedirect />} />
                    <Route path="/settings" element={<SettingsView />} />
                    <Route path="/stacktrace" element={<StackTracePage />} />
                    <Route path="/sql" element={<QueryBuilder />} />
                </Route>
                <Route path="/spocompute" element={<SpoComputeApp />} loader={UserLoader} id="spocompute" errorElement={<RootRouteError />}>
                    <Route index element={<Navigate to={computePath('cores-per-kdau')} replace />} />
                    {COMPUTE_TABS.map(tab => (
                        <Route key={tab.kind} path={tab.kind} element={<ComputeInsightsPage kind={tab.kind} />} />
                    ))}
                    <Route path="d/:dashboardId" element={<ConfigDashboardPage />} />
                    <Route path="*" element={<Navigate to={computePath('cores-per-kdau')} replace />} />
                </Route>
            </>
        )
    );
}

function RootRouteError() {
    const error = useRouteError();
    if (isAuthenticationRedirectInProgress(error)) {
        return <SigningOn />;
    }

    console.error('Unable to load the requested page', error);
    return <AuthenticationFailure />;
}


function BackfillRedirect() {
    const userInfo = useRouteLoaderData("root") as UserInfo | null;

    return (
        <>
            {((!userInfo) || (userInfo && !userInfo.isAuditor)) && <Navigate to="/allcommits" replace={true} />}
            {(userInfo) && (userInfo.isAuditor) && <BackFillView />}
        </>
    )
}

function DatabaseCopyRedirect() {
    const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo | null>(null);
    const [environmentLoading, setEnvironmentLoading] = useState(true);

    useEffect(() => {
        window.document.title = 'PR Guardian - Mirror';
    }, []);

    useEffect(() => {
        const fetchEnvironmentInfo = async () => {
            try {
                const response = await apiFetch('/api/environment');
                if (response.ok) {
                    const data = await response.json() as EnvironmentInfo;
                    setEnvironmentInfo(data);
                }
                else {
                    console.error('Failed to fetch environment info:', response.statusText);
                    // Default to hiding the feature if we can't determine environment
                    setEnvironmentInfo({ 
                        isDatabaseExtractEnabled: false,
                        environment: 'unknown'
                    });
                }
               
            } catch (error) {
                console.error('Error fetching environment info:', error);
                // Default to hiding the feature if we can't determine environment
                setEnvironmentInfo({ 
                    isDatabaseExtractEnabled: false,
                    environment: 'unknown'
                });
            } finally {
                setEnvironmentLoading(false);
            }
        };
        fetchEnvironmentInfo();
    }, []);

    if (environmentLoading) {
        return (
            <Card className="centered-card">
                <Text>Loading environment information...</Text>
            </Card>
        );
    }

    return (
        <>
            {environmentInfo?.isDatabaseExtractEnabled === false && <Navigate to="/" replace={true} />}
            {environmentInfo?.isDatabaseExtractEnabled === true && <CopyDatabaseManual />}
        </>
    );
}

function Login() {
    useEffect(() => {
        window.location.assign('/debuglogin');
    }, []);

    return null;
}

export default function App() {
    return (
        < FluentProvider theme={webLightTheme}>
            <ConfigLoader>
                <PageLayout />
            </ConfigLoader>
        </FluentProvider>)
}

function SpoComputeApp() {
    return (
        <FluentProvider theme={webLightTheme}>
            <ConfigLoader>
                <SpoComputeLayout />
            </ConfigLoader>
        </FluentProvider>
    );
}

function ConfigLoader({ children }: { children: ReactNode }) {
    const [config, setConfig] = useState<AppConfig | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        apiFetch("/api/config")
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to load configuration");
                }
                return response.json();
            })
            .then((data) => {
                if (isMounted) {
                    setConfig(data as AppConfig);
                }
            })
            .catch((err: unknown) => {
                if (isAuthenticationRedirectInProgress(err)) {
                    return;
                }
                if (isMounted) {
                    setError(err instanceof Error ? err.message : 'Failed to load configuration');
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    if (error) {
        return <div>Error loading configuration: {error}</div>;
    }

    if (!config) {
        return <div>Loading configuration...</div>;
    }

    return <ConfigProvider config={config}>{children}</ConfigProvider>;
}

async function UserLoader(): Promise<UserInfo | null> {
    if (window.user != null) {
        return window.user;
    }

    let user = await ApiHelper<UserInfo | null>("/api/userinfo");
    if (user === undefined) {
        user = null;
    }

    window.user = user;
    return user;
}
